import { useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import * as Sentry from '@sentry/react';
import { useNotification, useTranslation } from '@refinedev/core';
import {
  Button,
  Card,
  Flex,
  MultiSelect,
  NativeSelect,
  SegmentedControl,
  Select,
  Stack,
  Text,
  useComputedColorScheme,
  Alert,
} from '@mantine/core';
import { IconFilePlus, IconInfoCircle } from '@tabler/icons-react';

import { generateReportV2 } from 'api/action';
import { DatePickerWithPresets } from 'components/common/DatePickerWithPresets';
import type { Provider, Remittance } from 'interfaces';
import { ReportType } from 'interfaces';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  compareDateDesc,
  formatDateTimePattern,
  getCurrentMonthRange,
  getCutoffRange,
  parseDateTime,
} from 'utils/dateUtils';

interface ProviderGroupingReportModalContentProps {
  provider: Provider;
  remittances: Remittance[];
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  close?: () => void;
}

export const ProviderGroupingReportModalContent: React.FC<
  ProviderGroupingReportModalContentProps
> = ({ provider, remittances, setIsLoading, isLoading, close }) => {
  const colourMode = useComputedColorScheme('dark');
  const { open } = useNotification();
  const { translate } = useTranslation();
  const { id: siteID, timezone, province } = useCurrentSiteStore();
  const { id: userID } = useCurrentUserStore();

  const providerGroups = useMemo(
    () =>
      (provider.groups ?? [])
        .filter((group) => group.siteID === siteID)
        .sort((left, right) =>
          left.name.localeCompare(right.name, undefined, { numeric: true })
        ),
    [provider.groups, siteID]
  );
  const groupOptions = useMemo(
    () =>
      providerGroups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
    [providerGroups]
  );
  const remittanceOptions = useMemo(
    () =>
      remittances
        .filter((remittance) => remittance.siteID === siteID)
        .sort((left, right) =>
          compareDateDesc(String(left.parsedAt), String(right.parsedAt))
        )
        .map((remittance) => ({
          value: remittance.id,
          label: `${formatDateTimePattern(
            String(remittance.parsedAt),
            'yyyy-MM-dd HH:mm',
            {
              timezone,
              fallback: translate(
                'pages:reports.providerGroupingReport.unknownDate'
              ),
            }
          )}`,
        })),
    [remittances, siteID, timezone, translate]
  );

  const [selectedGroupIDs, setSelectedGroupIDs] = useState<string[]>(() =>
    providerGroups.map((group) => group.id)
  );
  const [dateType, setDateType] = useState<'serviceDate' | 'paymentDate'>(
    'paymentDate'
  );
  const [dateRange, setDateRange] = useState<[string, string]>(() =>
    getCurrentMonthRange(timezone)
  );
  const [selectedRemittanceID, setSelectedRemittanceID] = useState('');
  const [exportType, setExportType] = useState<'pdf' | 'csv'>('pdf');

  const selectedRemittance = remittances.find(
    (remittance) => remittance.id === selectedRemittanceID
  );
  const selectedRemittanceDate = selectedRemittance
    ? parseDateTime(String(selectedRemittance.parsedAt), { zone: timezone })
    : null;
  const selectedCutoffRange =
    selectedRemittanceDate?.isValid === true
      ? getCutoffRange(selectedRemittanceDate, undefined, province)
      : null;

  const isDateSelectionValid =
    dateType === 'paymentDate'
      ? Boolean(selectedRemittanceID && selectedCutoffRange)
      : Boolean(dateRange[0] && dateRange[1] && dateRange[0] <= dateRange[1]);
  const canCreate = selectedGroupIDs.length > 0 && isDateSelectionValid;

  const handleCreateReport = async () => {
    if (!canCreate) return;

    const startDate =
      dateType === 'paymentDate'
        ? selectedCutoffRange?.start.toISODate()
        : dateRange[0];
    const endDate =
      dateType === 'paymentDate'
        ? selectedCutoffRange?.end.toISODate()
        : dateRange[1];
    if (!startDate || !endDate) return;

    try {
      setIsLoading(true);
      await generateReportV2(ReportType.providerReport, {
        siteID,
        userID,
        providerID: provider.id,
        subtype: 'grouping',
        startDate,
        endDate,
        ...(dateType === 'paymentDate'
          ? { remittanceID: selectedRemittanceID }
          : {}),
        dateType,
        groupIDs: selectedGroupIDs,
        exportType,
        timeZone: timezone || 'UTC',
      });
      open?.({
        message: translate('pages:reports.generateReport.inProgress'),
        type: 'success',
        key: 'success',
      });
      close?.();
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        message: translate('pages:reports.generateReport.error'),
        type: 'error',
        key: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack gap="md">
      <Alert
        color="yellow"
        variant="light"
        title="Info"
        icon={<IconInfoCircle />}
      >
        {translate('pages:reports.disclaimer')}
      </Alert>
      <MultiSelect
        label={translate('pages:reports.providerGroupingReport.groups.label')}
        placeholder={translate(
          'pages:reports.providerGroupingReport.groups.placeholder'
        )}
        data={groupOptions}
        value={selectedGroupIDs}
        onChange={setSelectedGroupIDs}
        searchable
        required
      />
      {selectedGroupIDs.length === 0 && (
        <Text size="xs" c="red">
          {translate('pages:reports.providerGroupingReport.groups.required')}
        </Text>
      )}
      <Select
        label={translate('pages:reports.providerGroupingReport.dateType.label')}
        data={[
          {
            value: 'serviceDate',
            label: translate(
              'pages:reports.providerGroupingReport.dateType.serviceDate'
            ),
          },
          {
            value: 'paymentDate',
            label: translate(
              'pages:reports.providerGroupingReport.dateType.paymentDate'
            ),
          },
        ]}
        value={dateType}
        onChange={(value) => {
          const nextDateType =
            value === 'paymentDate' ? 'paymentDate' : 'serviceDate';
          setDateType(nextDateType);
          if (nextDateType === 'serviceDate') {
            setSelectedRemittanceID('');
          }
        }}
      />
      {dateType === 'paymentDate' ? (
        <NativeSelect
          label={translate(
            'pages:reports.providerGroupingReport.remittance.label'
          )}
          data={[
            {
              value: '',
              label: translate(
                'pages:reports.providerGroupingReport.remittance.placeholder'
              ),
            },
            ...remittanceOptions,
          ]}
          value={selectedRemittanceID}
          onChange={(event) =>
            setSelectedRemittanceID(event.currentTarget.value)
          }
        />
      ) : (
        <DatePickerWithPresets
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      )}
      <Card withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fw={600}>
            {translate(
              'pages:reports.providerGroupingReport.exportFormat.label'
            )}
          </Text>
          <SegmentedControl
            value={exportType}
            onChange={(value) => setExportType(value === 'csv' ? 'csv' : 'pdf')}
            data={[
              { label: translate('common:fileTypes.pdf'), value: 'pdf' },
              { label: translate('common:fileTypes.csv'), value: 'csv' },
            ]}
          />
        </Stack>
      </Card>
      <Flex gap="sm" justify="center">
        <Button
          variant="outline"
          onClick={close}
          color="gray"
          c={buttonTextColor(colourMode, isLoading)}
        >
          {translate('buttons.cancel')}
        </Button>
        <Button
          variant="outline"
          onClick={handleCreateReport}
          c={buttonTextColor(colourMode, isLoading)}
          color="green"
          leftSection={<IconFilePlus />}
          disabled={!canCreate || isLoading}
        >
          {translate('buttons.create')}
        </Button>
      </Flex>
    </Stack>
  );
};
